import { Alert, Box } from "@mui/material";
import { GlobalLoadingScreen } from "contexts/globalLoadingScreen";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { createContext, ReactNode, use, useEffect, useMemo } from "react";
import { backendApi } from "src/store/api/enhancedApi";
import { ScrapEyeMetadata } from "src/store/api/generatedApi";
import { GetScrapEyeMetadataResponse } from "src/store/api/generatedZodApi/scrap-eye-metadata";
import { z } from "zod";

const LOADED_SENTINEL = "__loaded__";

const allScrapEyeMetadataSchema = z.record(
  z.string(),
  z.union([GetScrapEyeMetadataResponse, z.literal(LOADED_SENTINEL)])
);

interface ScrapEyeMetadataContextValue {
  data: ScrapEyeMetadata;
  isLoading: boolean;
}

const ScrapEyeMetadataContext = createContext<
  ScrapEyeMetadataContextValue | undefined | null
>(undefined);

export const ProvideScrapEyeMetadata = ({
  children,
}: {
  children: ReactNode;
}) => {
  const context = use(ScrapEyeMetadataContext);
  if (context === undefined) {
    return (
      <ProvideScrapEyeMetadataInner>{children}</ProvideScrapEyeMetadataInner>
    );
  }
  return children;
};

const ProvideScrapEyeMetadataInner = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { t } = useTenantTranslation();
  const [allScrapEyeMetadata, setAllScrapEyeMetadata] = useLocalStorageState<
    Record<string, ScrapEyeMetadata | null | typeof LOADED_SENTINEL>
  >("scrapchefScrapEyeMetadataData", {}, { schema: allScrapEyeMetadataSchema });

  const tenantName = useTenant();
  const cachedData = allScrapEyeMetadata[tenantName];
  const isLoading = cachedData === undefined;

  const [getScrapEyeMetadata] =
    backendApi.endpoints.getScrapEyeMetadata.useLazyQuery();

  useEffect(() => {
    void getScrapEyeMetadata({ tenantName })
      .unwrap()
      .then((data) => {
        setAllScrapEyeMetadata((current) => ({
          ...current,
          [tenantName]: data ?? LOADED_SENTINEL,
        }));
      })
      .catch(() => {
        // Metadata is optional; fetch failures resolve as unavailable data.
        setAllScrapEyeMetadata((current) => ({
          ...current,
          [tenantName]: LOADED_SENTINEL,
        }));
      });
  }, [tenantName, getScrapEyeMetadata, setAllScrapEyeMetadata]);

  const data: ScrapEyeMetadata | null =
    cachedData === undefined ||
    cachedData === LOADED_SENTINEL ||
    cachedData === null
      ? null
      : cachedData;

  const value = useMemo(
    () => (data == null ? null : { data, isLoading }),
    [data, isLoading]
  );

  if (isLoading) {
    return <GlobalLoadingScreen />;
  }

  if (data == null) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="info">{t("scrapEyeDataNotAvailable")}</Alert>
      </Box>
    );
  }

  return (
    <ScrapEyeMetadataContext value={value}>{children}</ScrapEyeMetadataContext>
  );
};

export const useScrapEyeMetadata = (): ScrapEyeMetadata => {
  const context = use(ScrapEyeMetadataContext);

  if (context === undefined) {
    throw new Error(
      "useScrapEyeMetadata must be used within ProvideScrapEyeMetadataData"
    );
  }

  if (context === null || context.isLoading) {
    throw new Error("ScrapEyeMetadata is still loading");
  }

  return context.data;
};
